import { Box, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, date, daysBetween, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import { SectionCard, StatusChip } from '../../ui/primitives';

interface Deadline {
  id: string;
  date: string;
  title: string;
  detail: string;
  to: string;
}

export default function DeadlinesPanel() {
  const scope = useTenantScope();
  const { state } = useRegistry();
  const navigate = useNavigate();

  const deadlines = useMemo(() => {
    const items: Deadline[] = [];
    for (const call of scope.calls.filter((item) => item.status === 'ISSUED')) {
      const outstanding = call.lines.filter((line) => line.status !== 'PAID');
      items.push({
        id: call.id,
        date: call.dueDate,
        title: `${scope.fundById.get(call.fundId)?.code} Call ${call.number} due`,
        detail: `${outstanding.length} holders, ${money(sumBy(outstanding, (line) => line.amount - line.paid))} outstanding`,
        to: `/manager/calls/${call.id}`,
      });
    }
    for (const distribution of scope.distributions.filter(
      (item) => item.status !== 'PAID' && item.status !== 'REJECTED',
    )) {
      items.push({
        id: distribution.id,
        date: distribution.paymentDate,
        title: `${scope.fundById.get(distribution.fundId)?.code} distribution payable`,
        detail: `Period ending ${date(distribution.periodEnd)} · status ${distribution.status.toLowerCase().replace('_', ' ')}`,
        to: `/manager/distributions/${distribution.id}`,
      });
    }
    if (scope.tenant.id === 't-fides') {
      for (const report of state.atoReports.filter(
        (item) => item.status !== 'LODGED' && item.dueDate <= addDays(DEMO_TODAY, 120),
      )) {
        items.push({
          id: report.id,
          date: report.dueDate,
          title: `${report.kind.replace('_', ' ')} lodgement (${report.period})`,
          detail: `${report.records} records · ${report.status.toLowerCase().replace('_', ' ')}`,
          to: '/manager/tax',
        });
      }
    }
    for (const offer of scope.offers.filter((item) => item.status === 'PUBLISHED')) {
      items.push({
        id: offer.id,
        date: offer.closesAt.slice(0, 10),
        title: `${scope.fundById.get(offer.fundId)?.code} offer closes`,
        detail: offer.issueAtClose ? 'Then financial close and unit issue' : 'Applications close',
        to: `/manager/offers/${offer.id}`,
      });
    }
    const expiring = scope.holders.filter(
      (holder) =>
        holder.wholesale?.expiresAt &&
        holder.wholesale.expiresAt >= DEMO_TODAY &&
        holder.wholesale.expiresAt <= addDays(DEMO_TODAY, 60),
    );
    if (expiring.length) {
      items.push({
        id: 'ws',
        date: addDays(DEMO_TODAY, 45),
        title: 'Wholesale certificates expiring',
        detail: `${expiring.length} holders in the next 60 days`,
        to: '/manager/kyc',
      });
    }
    return items.sort((left, right) => left.date.localeCompare(right.date)).slice(0, 7);
  }, [scope, state.atoReports]);

  return (
    <SectionCard title="Coming up" subtitle="Next 120 days" noPadding>
      <List dense disablePadding>
        {deadlines.map((item) => {
          const days = daysBetween(DEMO_TODAY, item.date);
          return (
            <ListItemButton
              key={item.id}
              onClick={() => navigate(item.to)}
              sx={{ px: 2.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <Box sx={{ width: 56, flexShrink: 0, textAlign: 'center', mr: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.1 }}>
                  {date(item.date).split(' ')[0]}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {date(item.date).split(' ')[1]}
                </Typography>
              </Box>
              <ListItemText
                primary={item.title}
                secondary={item.detail}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
              />
              <StatusChip
                status={days < 0 ? 'OVERDUE' : days <= 7 ? 'PENDING' : 'OPEN'}
                label={days < 0 ? `${-days}d overdue` : days === 0 ? 'Today' : `${days}d`}
              />
            </ListItemButton>
          );
        })}
      </List>
    </SectionCard>
  );
}
